import React, { useEffect, useState } from "react";
import { Button, Divider, Select, Typography } from "antd";
import { PlusOutlined, ReloadOutlined, CopyOutlined } from "@ant-design/icons";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import widgetService from "../../../../Service/widget.service";
import { Widget as WidgetEntity } from "../../../../Entities/Widget";
import "./index.scss";

const positionOptions = [
  { label: "Top-Left", value: "topLeft" },
  { label: "Top-Center", value: "topCenter" },
  { label: "Top-Right", value: "topRight" },
  { label: "Middle-Left", value: "middleLeft" },
  { label: "Middle-Right", value: "middleRight" },
  { label: "Bottom-Left", value: "bottomLeft" },
  { label: "Bottom-Center", value: "bottomCenter" },
  { label: "Bottom-Right", value: "bottomRight" },
];

const Widget: React.FC = () => {
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const { isLoading, setLoading, setLoadingMessage } = useLoader();
  const [widget, setWidget] = useState<WidgetEntity | null>(null);

  const getScriptContent = (widget: WidgetEntity) => {
    return `<script src="${process.env.REACT_APP_API_BASE_URL}/widget/script?API_KEY=${widget.apiKey}&position=${widget.position}" defer></script>`;
  };

  const generateScript = async () => {
    setLoading(true);
    setLoadingMessage("Generating script...");
    try {
      if (selectedPosition) {
        const createdWidget = await widgetService.createOrUpdateWidget(
          selectedPosition
        );
        if (createdWidget) {
          setWidget(createdWidget);
          setSelectedPosition(createdWidget?.position);
        }
      }
    } catch (error) {
      console.error("Error generating script:", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!widget) {
      openNotification("error", "Error", "No widget available to copy");
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(getScriptContent(widget));
        openNotification("success", "Copied", "Script copied to clipboard");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = getScriptContent(widget);
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification("success", "Copied", "Script copied to clipboard");
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy script",
        "Please copy the script manually from the code block"
      );
    }
  };

  useEffect(() => {
    const fetchWidget = async () => {
      const widget = await widgetService.getWidget();
      if (widget) {
        setWidget(widget);
        setSelectedPosition(widget.position);
      }
    };
    fetchWidget();
  }, []);

  return (
    <section className="integration-tab-content-section">
      {/* Section Title and Divider */}
      <div className="integration-details-section-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Widget Integration
        </Typography.Title>

        <Divider style={{ margin: "0" }} />
      </div>

      {/* Section Content */}
      <section className="integration-tab-content-section-content">
        <div>
          <Typography.Paragraph>
            Easily integrate the <strong>QualifyMe</strong> Careers Widget into
            your website for enhanced visibility and applicant engagement.{" "}
            <br></br>To integrate the widget:
          </Typography.Paragraph>
          <ol>
            <li>
              Choose the desired widget <strong>position</strong> from the
              dropdown menu.
            </li>
            <li>
              Click the <strong>Generate</strong> button to create your
              customized <strong>&lt;script&gt;</strong>.
            </li>
            <li>
              Copy the generated <strong>&lt;script&gt;</strong> code.
            </li>
            <li>
              Paste it at the end of the <strong> &lt;body&gt;</strong> section
              of your website's home page.
            </li>
          </ol>
          <Typography.Paragraph>
            Once added, the careers widget will be live and interactive on your
            site, connecting candidates directly to your job opportunities
            hosted
          </Typography.Paragraph>
        </div>

        <Divider orientation="center" style={{ margin: "0" }} />

        <Typography.Text>
          Please choose a position to place the widget on your website for
          optimal visibility and functionality.
        </Typography.Text>

        {/* Position Selection */}
        <div className="widget-position-selection-form">
          <Select
            style={{ maxWidth: "350px", width: "100%" }}
            placeholder="Select Position"
            value={selectedPosition}
            onChange={(value) => setSelectedPosition(value)}
            options={positionOptions}
            showSearch
            filterOption={(input: string, option: any) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />

          <Button
            children={widget ? "Regenerate" : "Generate"}
            type="primary"
            size="middle"
            onClick={generateScript}
            loading={isLoading}
            disabled={selectedPosition === null || selectedPosition === ""}
            icon={widget ? <ReloadOutlined /> : <PlusOutlined />}
          />
        </div>

        {widget && (
          <Divider orientation="center" style={{ margin: "0" }} />
        )}

        {widget && (
          <Typography.Text>
            Copy the following code at the end of the{" "}
            <strong> &lt;body&gt;</strong> section of your website's home page
          </Typography.Text>
        )}

        {widget && (
          <div className="code-container">
            <div className="code-header">
              <span className="code-label">Script:</span>
              <Button
                type="primary"
                onClick={handleCopyToClipboard}
                icon={<CopyOutlined />}
              >
                Copy
              </Button>
            </div>

            <div className="code-body">
              <pre id="code">
                <code>{`<script src="${process.env.REACT_APP_API_BASE_URL}/widget/script?API_KEY=${widget.apiKey}&position=${widget.position}" defer></script>`}</code>
              </pre>
            </div>
          </div>
        )}
      </section>
      
    </section>
  );
};

export default Widget;
